// Proactive 'enable notifications' card. Surfaces after the user has been in
// the inbox for ~8s on a device + permission state where it can actually
// succeed, OR once they explicitly engage with a thread. Respects dismissals
// (snoozed for 7 days via localStorage) and never re-asks if permission has
// already been granted or denied. iOS Safari outside standalone is handled
// with an explanatory hint instead of a useless "Enable" button.

import { useEffect, useState } from 'react';
import { Bell, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { currentSubscription, enablePush, readPushState } from '@/lib/push-client';
import { toast } from 'sonner';

const DISMISS_KEY = 'wharf:notif-prompt-dismissed-at';
const DISMISS_DAYS = 7;
const REVEAL_DELAY_MS = 8_000;

function dismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* localStorage blocked — we'll just nag once more next session */
  }
}

export function NotificationPrompt() {
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState(() => readPushState());
  const [enabling, setEnabling] = useState(false);

  // Decide whether to surface the prompt. Re-check after the delay so users
  // who already toggled it via the sidebar in the same session never see it.
  useEffect(() => {
    if (dismissedRecently()) return;
    if (!state.supported) return;
    if (state.permission === 'denied') return; // respectful — don't keep asking
    let cancelled = false;

    // Check whether we already have a subscription on this device.
    void currentSubscription().then((sub) => {
      if (cancelled || sub) return;
      // Don't pop the prompt the instant the inbox mounts — give the user a
      // few seconds to orient first.
      const t = setTimeout(() => {
        if (cancelled) return;
        // Re-read in case state shifted while we waited (granted via sidebar).
        const s = readPushState();
        if (s.permission === 'granted' && !s.needsInstall) {
          // Permission is granted but no subscription yet — auto-subscribe
          // silently and skip the prompt entirely.
          void enablePush().then(() => setState(readPushState()));
          return;
        }
        setState(s);
        setVisible(true);
      }, REVEAL_DELAY_MS);
      return () => clearTimeout(t);
    });

    return () => {
      cancelled = true;
    };
  }, [state.supported, state.permission]);

  if (!visible) return null;

  async function enable() {
    setEnabling(true);
    try {
      const ok = await enablePush();
      if (ok) {
        toast.success('Notifications on. We’ll ping you for new mail + mentions.');
        setVisible(false);
      } else {
        toast.error('Couldn’t enable notifications. Check browser permissions.');
        setVisible(false);
      }
    } finally {
      setEnabling(false);
    }
  }

  function dismiss() {
    markDismissed();
    setVisible(false);
  }

  return (
    <div className="border-b bg-gradient-to-br from-primary/5 via-background to-background px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Bell className="size-4" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-semibold">
            Get notified the moment it matters
            <Sparkles className="size-3 text-amber-500" strokeWidth={2.5} />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {state.needsInstall
              ? 'On iPhone you need to install Wharf Mail first — tap Share → Add to Home Screen, then enable notifications from there.'
              : 'New mail in the inbox and @-mentions from teammates pinged straight to your device. Works on this browser too.'}
          </p>
          <div className="mt-2 flex items-center gap-2">
            {!state.needsInstall && (
              <Button size="sm" onClick={enable} disabled={enabling} className="h-7 gap-1.5 text-xs">
                {enabling ? 'Enabling…' : 'Enable notifications'}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              disabled={enabling}
              className="h-7 text-xs text-muted-foreground"
            >
              {state.needsInstall ? 'Got it' : 'Not now'}
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded p-1 text-muted-foreground/70 hover:bg-secondary hover:text-foreground"
        >
          <X className="size-3.5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
